import dayjs from 'dayjs';
import { get, isEmpty, isEqual, mergeWith, omit, toString } from 'lodash';
import {
  DEFAULT_DURATION,
  ISO_DATE_FORMAT,
  NAVIGATION_STATE,
} from 'src/constants';
import { BASE_CONFIG } from 'src/constants/gcfSectionConfig';
import { PATHS } from 'src/constants/paths';
import { getOrCreateSectionId } from 'src/hooks/api/layouts/useCreateScreenSection';
import {
  DyProductSource,
  IDisclaimerPosition,
  IDisclaimerWidth,
  ILayout,
  LayoutTypeIds,
  ProjectConfigInterface,
  SectionTypes,
} from 'src/interfaces';
import {
  ICreateScheduleItem,
  ISchedule,
  IScheduleConfig,
  IUpdateScheduleItem,
} from 'src/interfaces/Schedule';
import {
  getGridDimensionCalculations,
  getSectionSizeAndPositionForLayoutBuilder,
  lang,
  parseLayoutConfig,
} from 'src/utils';
import { FileTypes, getFileExtensionType } from 'src/utils/file-types';
import { uid } from 'uid';

import {
  getPositions,
  getScreensGridSections,
} from '../scheduleLocationOverridesPage/ScheduleLocationOverridePage.helper';
import {
  BuildChangeSetArgs,
  ChangeItem,
  ChangeOperation,
  ChangeSetPayload,
  ChangeTypeEnum,
  IDefaultSection,
  IGridSection,
  IPositionGridLines,
  IPredefinedSection,
  IScheduledSectionBackupComponent,
  IScheduledSectionComponent,
  TScreensGridSections,
  TSectionPayload,
  TSuggestedComponentTypes,
} from './ScheduleComponentPage.interface';

export const getLayerScreensDisclaimerInfo = (
  gridLines: string[] | undefined,
  screenIds: number[],
) => {
  if (!gridLines) return {};

  const screensDisclaimerInfo: Record<string, IPredefinedSection[]> = {};

  gridLines.forEach((gridLine, index) => {
    const { orientation, rows, cols, disclaimerPosition, disclaimerType } =
      parseLayoutConfig(gridLine);
    const { disclaimerHeightOriginal, disclaimerWidthOriginal, gridHeight } =
      getGridDimensionCalculations({
        orientation,
        disclaimerPosition,
        disclaimerType,
        rows,
        cols,
      });
    const topOffset =
      disclaimerPosition === IDisclaimerPosition.TOP ? 0 : gridHeight;

    if (disclaimerType === IDisclaimerWidth.FULL) {
      screensDisclaimerInfo[index] = [
        {
          i: uid(),
          name: `${lang.disclaimer} 1`,
          height: disclaimerHeightOriginal,
          width: disclaimerWidthOriginal,
          leftOffset: 0,
          topOffset,
          config: BASE_CONFIG,
          isDisclaimer: true,
          canBeDeleted: false,
          screenId: screenIds[index],
        },
      ];
    } else if (disclaimerType === IDisclaimerWidth.HALF) {
      screensDisclaimerInfo[index] = [
        {
          i: uid(),
          name: `${lang.disclaimer} 1`,
          height: disclaimerHeightOriginal,
          width: disclaimerWidthOriginal,
          leftOffset: 0,
          topOffset,
          config: BASE_CONFIG,
          isDisclaimer: true,
          canBeDeleted: false,
          screenId: screenIds[index],
        },
        {
          i: uid(),
          name: `${lang.disclaimer} 2`,
          height: disclaimerHeightOriginal,
          width: disclaimerWidthOriginal,
          leftOffset: disclaimerWidthOriginal,
          topOffset,
          config: BASE_CONFIG,
          isDisclaimer: true,
          canBeDeleted: false,
          screenId: screenIds[index],
        },
      ];
    } else {
      screensDisclaimerInfo[index] = [];
    }
  });

  return screensDisclaimerInfo;
};

export const shouldCoverAllScreen = (
  currentGridLine: string,
  rawGridLine: string,
) => {
  const hasDisclaimerChange =
    currentGridLine?.includes('Disclaimer') ||
    rawGridLine?.includes('Disclaimer');

  return currentGridLine !== rawGridLine && hasDisclaimerChange;
};

export const getSectionsId = async (
  sectionsList: (IGridSection | IDefaultSection)[],
) => {
  const sectionsIdMap = new Map<string, number>();
  const sectionsMap = new Map<string, { width: number; height: number }>();

  sectionsList.forEach(({ width, height }) => {
    const sectionKey = `${width}-${height}`;

    if (!sectionsMap.get(sectionKey)) {
      sectionsMap.set(sectionKey, {
        width: width!,
        height: height!,
      });
    }
  });

  return Promise.all(
    Array.from(sectionsMap.values()).map(({ width, height }) =>
      getOrCreateSectionId({
        width: width!,
        height: height!,
      }),
    ),
  ).then((res) => {
    Array.from(sectionsMap.keys()).forEach((key, index) => {
      sectionsIdMap.set(key, res[index]);
    });

    return sectionsIdMap;
  });
};

export const getScheduleComponentsPayload = async <
  T extends ICreateScheduleItem | IUpdateScheduleItem,
>(
  positionsGridLines: IPositionGridLines[],
  screensGridSections: TScreensGridSections,
) => {
  let sectionsList: TSectionPayload[] = [];
  const scheduleComponentsList: T[] = [];

  Object.entries(screensGridSections).forEach(([position, layerData]) => {
    Object.entries(layerData).forEach(([screenIndex, screenSections]) => {
      const channelSequence = Number(screenIndex) + 1;
      const currentPosition = positionsGridLines.find(
        (item) => Number(item.position) === Number(position),
      ) as IPositionGridLines;

      sectionsList.push(
        ...screenSections.map((section) => ({
          ...section,
          channel_screen_sequence: channelSequence,
          position: currentPosition?.position,
          duration: currentPosition?.duration,
        })),
      );
    });
  });

  sectionsList = sectionsList.filter((section) => !isEmpty(section.components));

  const sectionsIdMap = await getSectionsId(sectionsList);

  for (const section of sectionsList) {
    const sectionId = sectionsIdMap.get(`${section.width}-${section.height}`);

    [...(section.components as IScheduledSectionComponent[])]?.forEach(
      (component) => {
        if (sectionId) {
          const scheduleComponent = {
            ...(component.id && { id: component.id }),
            channel_screen_sequence: section.channel_screen_sequence as number,
            component_id: component.component_id,
            left_offset: section.leftOffset as number,
            top_offset: section.topOffset as number,
            width: section.width as number,
            height: section.height as number,
            start_date: component.scheduleStartDate!,
            end_date: component.scheduleEndDate!,
            position: section?.position,
            duration: section?.duration,
            section_config: section.config,
            config: omit(component.config, 'duration'),
            backups:
              component.backups?.map((item) => ({
                ...(item.id && { id: item.id }),
                component_id: item.component_id,
                config: omit(item.config, 'duration'),
                duration: getBackupsDuration(item, section?.duration),
                backup_order: item.backup_order,
              })) ?? [],
            allow_overrides: component.allow_overrides as boolean,
          } as unknown as T;

          scheduleComponentsList.push(scheduleComponent);
        }
      },
    );
  }

  return scheduleComponentsList;
};

export const getBackupsDuration = (
  item: IScheduledSectionBackupComponent,
  positionDuration: number,
) => {
  const { component, config } = item;

  if (component?.config?.background) {
    const componentBackgroundType = getFileExtensionType(
      component?.config?.background,
    );

    if (componentBackgroundType === FileTypes.VIDEO) {
      return config?.duration as number;
    }
  }

  return positionDuration;
};

export const getDYRSectionInfo = (
  layout: ILayout,
): Record<string, IGridSection[]> => {
  if (!layout.config?.sectionTypes?.includes(SectionTypes.DYR)) {
    return {
      2: [],
    };
  }

  const DYRSections: IGridSection[] = layout.screens[2].sections
    .filter((section) =>
      (
        section.config?.suggestedComponentTypes as TSuggestedComponentTypes
      )?.includes(DyProductSource.DYR),
    )
    .map(({ height, width, left_offset, top_offset, config, screen_id }) => ({
      i: uid(),
      name: lang.editLayoutsSidebar.recommendation,
      height,
      width,
      leftOffset: left_offset,
      topOffset: top_offset,
      config,
      isDisclaimer: false,
      isDYR: true,
      canBeDeleted: false,
      screenId: screen_id,
      ...getSectionSizeAndPositionForLayoutBuilder(
        layout.config!.gridLines![2],
        width,
        height,
        left_offset,
        top_offset,
      ),
    }));

  return {
    2: DYRSections,
  };
};

export const areAllScreensValid = (
  screensGridSections: TScreensGridSections,
  positionsGridLines: IPositionGridLines[],
  rawConfigForGridLines: ILayout['config'],
) => {
  const layerIndexes = Object.keys(screensGridSections);

  for (const layerIndex of layerIndexes) {
    const defaultScreens = screensGridSections[layerIndex] || {};
    const screenIndexes = Object.keys(defaultScreens);

    if (screenIndexes.length === 0) return false;

    const areEmpty = areAllScreensEmpty(screensGridSections);

    if (areEmpty) return false;

    for (const screenIndex of screenIndexes) {
      const screenSections = groupSectionsByScreenIndex(
        screensGridSections,
        screenIndex,
      );

      const rawGridLine = groupGridLinesByScreenIndex(
        rawConfigForGridLines?.gridLines as string[],
        screenIndex,
      );

      const currentPositionGridLines = positionsGridLines.find(
        (item) => item.position === Number(layerIndex),
      )?.gridLines;

      const gridLine = groupGridLinesByScreenIndex(
        currentPositionGridLines as string[],
        screenIndex,
      );

      if (!isScreenValid(screenSections[layerIndex], gridLine, rawGridLine)) {
        return false;
      }
    }
  }

  return true;
};

export const isScreenValid = (
  screenSections: (IGridSection | IDefaultSection)[],
  currentGridLine: string | undefined,
  rawGridLine: string | undefined,
) => {
  if (!currentGridLine || !rawGridLine) return false;

  const allSections = Object.values(screenSections).flat();

  /* If no scheduled sections, it considers as valid */
  if (isEmpty(allSections)) return true;

  const { predefinedSections, scheduledSections } =
    splitPredefinedSections(allSections);

  /* If any predefined section has been turned on but no components, invalid */
  if (
    predefinedSections.some(
      (section) => section.canBeScheduled && isEmpty(section.components),
    )
  ) {
    return false;
  }

  /* All scheduled sections must have components */
  if (scheduledSections.some((section) => isEmpty(section.components))) {
    return false;
  }

  return true;
};

const splitPredefinedSections = (
  sections: (IGridSection | IDefaultSection)[],
) => {
  return sections.reduce(
    (
      acc: {
        predefinedSections: (IGridSection | IDefaultSection)[];
        scheduledSections: (IGridSection | IDefaultSection)[];
      },
      section,
    ) => {
      if (!section.canBeDeleted) {
        acc.predefinedSections.push(section);
      } else {
        acc.scheduledSections.push(section);
      }

      return acc;
    },
    { predefinedSections: [], scheduledSections: [] },
  );
};

export const isScreenFullyDrawn = (
  sections: (IGridSection | IDefaultSection)[],
  currentGridLine: string,
) => {
  const sectionsWithoutDisclaimers = sections.filter(
    (section) => !section.isDisclaimer,
  );

  const { rows, cols } = parseLayoutConfig(currentGridLine);

  const allGrids = sectionsWithoutDisclaimers.reduce(
    (sum, { w, h }) => (sum += (w ?? 0) * (h ?? 0)),
    0,
  );

  return allGrids === rows * cols;
};

const areAllScreensEmpty = (screensGridSections: TScreensGridSections) => {
  for (const screens of Object.values(screensGridSections)) {
    for (const sections of Object.values(screens)) {
      for (const section of sections) {
        if (!isEmpty(section.components)) {
          return false;
        }
      }
    }
  }

  return true;
};

const groupSectionsByScreenIndex = (
  screensGridSections: TScreensGridSections,
  screenIndex: string,
) => {
  const result: Record<string, (IGridSection | IDefaultSection)[]> = {} as any;

  Object.entries(screensGridSections).forEach(([layerIndex, layerScreens]) => {
    result[layerIndex] = layerScreens[screenIndex] || [];
  });

  return result;
};

const groupGridLinesByScreenIndex = (
  gridLines: string[],
  screenIndex: string,
) => {
  return gridLines?.[Number(screenIndex)];
};

export const getPredefinedSectionsForDefaultLayer = (layout: ILayout) => {
  const isOutdoorType = layout.layout_type_id === LayoutTypeIds.OUTDOOR;

  return isOutdoorType
    ? mergeWith(
        getLayerScreensDisclaimerInfo(
          layout.config?.gridLines,
          layout.screens.map(({ id }) => id),
        ),
        getDYRSectionInfo(layout),
        (objValue, srcValue) => objValue.concat(srcValue),
      )
    : getLayerScreensDisclaimerInfo(
        layout.config?.gridLines,
        layout.screens.map(({ id }) => id),
      );
};

export const isComponentDateRangeValid = (
  startDateStr?: string | null,
  endDateStr?: string | null,
  isOwnerOperator?: boolean,
): boolean => {
  const startDate = startDateStr?.trim() ? dayjs(startDateStr) : null;
  const endDate = endDateStr?.trim() ? dayjs(endDateStr) : null;

  if (!startDate || !startDate.isValid()) return false;
  if (isOwnerOperator && !endDate) {
    return false;
  }
  if (endDate) {
    if (!endDate.isValid()) return false;

    if (startDate.isAfter(endDate)) return false;
  }

  return true;
};

export const hasInvalidOrDuplicateDate = (
  components: IScheduledSectionComponent[],
  isOwnerOperator?: boolean,
): boolean => {
  const seen = new Set<string>();

  return components.some((component) => {
    const startDate = component?.scheduleStartDate?.trim();
    const endDate = component?.scheduleEndDate?.trim();
    const componentExpiredDate = component?.component?.end_date;

    if (!isComponentDateRangeValid(startDate, endDate)) {
      return true;
    }

    if (startDate) {
      if (seen.has(startDate)) {
        return true;
      }
      seen.add(startDate);
    }

    if (componentExpiredDate) {
      const endBoundary = dayjs(componentExpiredDate);

      if (startDate && dayjs(startDate).isAfter(endBoundary)) return true;
      if (endDate && dayjs(endDate).isAfter(endBoundary)) return true;
    }
    if (isOwnerOperator && component.override) {
      const overrideStart = component.override.start_date?.trim?.();
      const overrideEnd = component.override.end_date?.trim?.();
      if (
        !isComponentDateRangeValid(overrideStart, overrideEnd, isOwnerOperator)
      ) {
        return true;
      }
    }

    return false;
  });
};

export const getNewPositionsGridLines = (
  prevPositionGridLines: IPositionGridLines[],
  newData: Partial<IPositionGridLines> & { position: number },
) => {
  const existingIndex = prevPositionGridLines.findIndex(
    (item) => item.position === newData.position,
  );

  if (existingIndex === -1) {
    const newItem: IPositionGridLines = {
      duration: DEFAULT_DURATION,
      ...newData,
    } as IPositionGridLines;

    return [...prevPositionGridLines, newItem];
  }

  return prevPositionGridLines.map((item, index) =>
    index === existingIndex ? { ...item, ...newData } : item,
  );
};

export const getCurrentPositionGridLines = (
  positionsGridLines: IPositionGridLines[],
  layout: ILayout | undefined,
  layerIndex: number,
  previewingPositionIndex: number,
) => {
  if (!layout) return [];

  const positionIndex =
    layerIndex === -1 ? previewingPositionIndex : layerIndex;

  // return a default value
  if (positionIndex === -1) return layout.config?.gridLines as string[];

  return positionsGridLines.find((item) => item.position === positionIndex)
    ?.gridLines as string[];
};

export const getReviewPageProps = (
  screensGridSections: TScreensGridSections,
) => {
  const componentNamesSet = new Set<string>();
  const positionsWithComponentSet = new Set<string>();
  const scheduleStartDates: string[] = [];

  Object.entries(screensGridSections).forEach(([layerIndex, layerSection]) => {
    Object.values(layerSection).forEach((sections) => {
      sections.forEach((section) => {
        section.components?.forEach((compWrapper) => {
          positionsWithComponentSet.add(layerIndex);

          if (compWrapper?.scheduleStartDate) {
            scheduleStartDates.push(compWrapper.scheduleStartDate);
          }

          const name = compWrapper?.component?.name;

          if (name) {
            componentNamesSet.add(name);
          }

          compWrapper?.backups?.forEach((backupCompWrapper) => {
            const name = backupCompWrapper?.component?.name;
            if (name) {
              componentNamesSet.add(name);
            }
          });
        });
      });
    });
  });

  const earliestScheduleStartDate = [...scheduleStartDates].sort()[0];

  return {
    componentNames: Array.from(componentNamesSet),
    earliestScheduleStartDate,
    positionString: `${lang.position} ${Array.from(
      positionsWithComponentSet,
    ).join(', ')}`,
  };
};

export const initializeScreensGridSections = (
  layout: ILayout,
  projectConfig?: Partial<ProjectConfigInterface>,
  schedule?: ISchedule,
) => {
  const positions = getPositions(layout, projectConfig, schedule);

  const gridLines = getScreensGridSections(
    layout,
    positions,
    schedule?.scheduleItems,
  );

  return { positions, gridLines };
};

export const getInvalidBackups = (
  scheduledEndDate: string | null = null,
  currentBackups?: IScheduledSectionBackupComponent[],
) => {
  if (!currentBackups) return [];

  return currentBackups
    .filter((backup) => {
      const { component: { end_date } = {} } = backup;

      if (scheduledEndDate === null) {
        return end_date && end_date !== null;
      }

      if (end_date && end_date !== null) {
        return dayjs(scheduledEndDate).isAfter(dayjs(end_date));
      }

      return false;
    })
    .map((backup) => backup.uid);
};

export const hasInvalidBackupsInSection = (
  section: IGridSection | IDefaultSection,
) => {
  if (isEmpty(section.components)) return false;

  for (const component of section.components as IScheduledSectionComponent[]) {
    if (isEmpty(component.backups)) continue;

    if (
      !isEmpty(getInvalidBackups(component.scheduleEndDate, component.backups))
    ) {
      return true;
    }
  }

  return false;
};

export const areAllBackupsValid = (
  screensGridSections: TScreensGridSections,
) => {
  for (const screens of Object.values(screensGridSections)) {
    for (const sections of Object.values(screens)) {
      for (const section of sections) {
        if (hasInvalidBackupsInSection(section)) {
          return false;
        }
      }
    }
  }

  return true;
};

export const generateScheduleConfigPayload = (
  layout?: ILayout,
  positions: IPositionGridLines[] = [],
): { schedule_config: IScheduleConfig | null } => {
  const originalGridLines = get(layout, 'config.gridLines', [] as string[]);

  const diff: Record<string, string[]> = positions.reduce(
    (acc, { position, gridLines: updatedGridLines = [] }) => {
      if (!isEqual(updatedGridLines, originalGridLines)) {
        acc[toString(position)] = updatedGridLines;
      }

      return acc;
    },
    {} as Record<string, string[]>,
  );

  if (isEmpty(diff)) return { schedule_config: null };

  return { schedule_config: { gridLines: diff } };
};

export const hasScheduleChanged = (
  initial: {
    positions: IPositionGridLines[];
    screensGridSections: TScreensGridSections;
    scheduleName?: string;
  },
  current: {
    positions: IPositionGridLines[];
    screensGridSections: TScreensGridSections;
    scheduleName?: string;
  },
): boolean => {
  const selectComponents = (
    input: TScreensGridSections,
  ): TScreensGridSections => {
    const result: TScreensGridSections = {};

    for (const positionKey in input) {
      result[positionKey] = {};

      for (const screenIndex in input[positionKey]) {
        result[positionKey][screenIndex] = input[positionKey][screenIndex].map(
          (section) => ({
            i: section.i,
            components: section.components ?? [],
          }),
        );
      }
    }

    return result;
  };

  const positionsChanged = !isEqual(initial.positions, current.positions);

  const screensChanged = !isEqual(
    selectComponents(initial.screensGridSections),
    selectComponents(current.screensGridSections),
  );

  const scheduleNameChanged =
    initial.scheduleName !== undefined &&
    current.scheduleName !== undefined &&
    initial.scheduleName.trim() !== current.scheduleName.trim();

  return positionsChanged || screensChanged || scheduleNameChanged;
};
export const buildChangeSetPayload = ({
  locationGroupIds,
  daypartId,
  scheduleChanges = [],
  scheduleItemChanges = [],
  scheduleItemOverrideChanges = [],
}: BuildChangeSetArgs): ChangeSetPayload => {
  return {
    location_group_ids: locationGroupIds,
    ...(daypartId !== undefined && { daypart_id: daypartId }),
    changes: [
      ...scheduleChanges,
      ...scheduleItemChanges,
      ...scheduleItemOverrideChanges,
    ],
  };
};

export const buildChangeItemsFromScreensGridSections = (
  initial: {
    positions: IPositionGridLines[];
    screensGridSections: TScreensGridSections;
    scheduleName?: string;
  },
  current: {
    positions: IPositionGridLines[];
    screensGridSections: TScreensGridSections;
    scheduleName?: string;
  },
): ChangeItem[] => {
  const changes: ChangeItem[] = [];

  /* =====================================================
     NEW: global component collectors (for MOVE detection)
     ===================================================== */
  const collectComponents = (grid: TScreensGridSections) =>
    new Map<number, IScheduledSectionComponent>(
      Object.values(grid)
        .flatMap(Object.values)
        .flatMap((sections) => sections)
        .flatMap((section) => section.components ?? [])
        .filter((c): c is IScheduledSectionComponent & { id: number } => !!c.id)
        .map((c) => [c.id, c]),
    );

  const initialComponentMap = collectComponents(initial.screensGridSections);
  const currentComponentMap = collectComponents(current.screensGridSections);

  /* ------------------ Position Resolver ------------------ */
  const positionMap = new Map<number, IPositionGridLines>();
  [...initial.positions, ...current.positions].forEach((p) =>
    positionMap.set(Number(p.position), p),
  );
  const getPosition = (screenId: string) => positionMap.get(Number(screenId));

  /* ------------------ Change Item Helper ------------------ */
  const createChangeItem = (
    op: ChangeOperation,
    componentId?: number,
    scheduleItemId?: number,
    extraData: Record<string, unknown> = {},
  ): ChangeItem => ({
    type: ChangeTypeEnum.SCHEDULE_ITEM,
    op,
    data: {
      id: scheduleItemId,
      component_id: componentId,
      ...extraData,
    },
  });

  const UPDATE_FIELD_MAP: Record<string, string> = {
    scheduleStartDate: 'start_date',
    scheduleEndDate: 'end_date',
    leftOffset: 'left_offset',
    topOffset: 'top_offset',
  };

  /* ------------------ Diff Helper ------------------ */
  const diffObjects = <T>(
    currentObj: T,
    initialObj: T,
    fieldMap: Record<string, string> = {},
  ) => {
    const diff: Record<string, unknown> = {};

    for (const key in currentObj) {
      if (key === 'component') continue;
      if (!isEqual(currentObj[key], initialObj[key])) {
        diff[fieldMap[key] ?? key] = currentObj[key];
      }
    }

    return diff;
  };

  const buildInsertPayload = (
    comp: IScheduledSectionComponent,
    section: any,
  ) => ({
    uid: section.i,
    channel_screen_sequence: section.channel_screen_sequence,
    left_offset: section.leftOffset ?? 0,
    top_offset: section.topOffset ?? 0,
    width: section.width,
    height: section.height,
    start_date: comp.scheduleStartDate ?? null,
    end_date: comp.scheduleEndDate ?? null,
    position: section.position,
    duration: section.duration,
    section_config: section.config ?? {},
    config: omit(comp.config, 'duration'),
    backups:
      comp.backups?.map((b) => ({
        id: b.id,
        component_id: b.component_id,
        config: omit(b.config, 'duration'),
        duration: getBackupsDuration(b, section.duration),
        backup_order: b.backup_order,
      })) ?? [],
    allow_overrides: comp.allow_overrides ?? false,
  });

  /* ------------------ Component Diff (MINIMALLY MODIFIED) ------------------ */
  const diffComponents = (
    currentComps: IScheduledSectionComponent[],
    initialComps: IScheduledSectionComponent[],
    sectionContext: any,
  ) => {
    const localChanges: ChangeItem[] = [];

    const curMap = new Map(currentComps.map((c) => [c.component?.id, c]));
    const initMap = new Map(initialComps.map((c) => [c.component?.id, c]));

    /* -------- INSERT or UPDATE -------- */
    for (const [compKey, cur] of curMap) {
      const init = initMap.get(compKey);

      if (!init) {
        // 👇 NEW: if existed before, it's a MOVE → UPDATE
        if (initialComponentMap.has(cur.id)) {
          localChanges.push(
            createChangeItem(ChangeOperation.UPDATE, cur.component_id, cur.id, {
              left_offset: sectionContext.leftOffset,
              top_offset: sectionContext.topOffset,
              channel_screen_sequence: sectionContext.channel_screen_sequence,
              position: sectionContext.position,
              duration: sectionContext.duration,
              start_date: cur.scheduleStartDate ?? null,
              end_date: cur.scheduleEndDate ?? null,
              backups:
                cur.backups?.map((b) => ({
                  id: b.id,
                  component_id: b.component_id,
                  config: omit(b.config, 'duration'),
                  duration: getBackupsDuration(b, sectionContext.duration),
                  backup_order: b.backup_order,
                })) ?? [],
            }),
          );
        } else {
          // true INSERT
          localChanges.push(
            createChangeItem(
              ChangeOperation.INSERT,
              cur.component_id,
              cur.id,
              buildInsertPayload(cur, sectionContext),
            ),
          );
        }
      } else {
        const diff = diffObjects(cur, init, UPDATE_FIELD_MAP);
        if (Object.keys(diff).length) {
          localChanges.push(
            createChangeItem(
              ChangeOperation.UPDATE,
              cur.component_id,
              cur.id,
              diff,
            ),
          );
        }
      }
    }

    /* -------- DELETE -------- */
    for (const [compKey, init] of initMap) {
      // 👇 NEW: if still exists elsewhere, do NOT delete
      if (!curMap.has(compKey) && !currentComponentMap.has(init.id)) {
        localChanges.push(
          createChangeItem(ChangeOperation.DELETE, init.component_id, init.id),
        );
      }
    }

    return localChanges;
  };

  /* ------------------ Position Duration Diff ------------------ */
  initial.positions.forEach((p) => {
    const cur = current.positions.find((c) => c.position === p.position);
    if (cur && cur.duration !== p.duration) {
      changes.push({
        type: ChangeTypeEnum.SCHEDULE_ITEM,
        op: ChangeOperation.UPDATE,
        data: { position: cur.position, duration: cur.duration },
      });
    }
  });

  /* ------------------ Section Diff (UNCHANGED) ------------------ */
  const mapSectionsById = (sections: any[] = []) =>
    new Map(sections.filter((s) => s?.i).map((s) => [s.i, s]));

  const allScreenIds = new Set([
    ...Object.keys(initial.screensGridSections),
    ...Object.keys(current.screensGridSections),
  ]);

  for (const screenId of allScreenIds) {
    const curScreen = current.screensGridSections[screenId] ?? {};
    const initScreen = initial.screensGridSections[screenId] ?? {};
    const sectionIndexes = new Set([
      ...Object.keys(curScreen),
      ...Object.keys(initScreen),
    ]);
    const position = getPosition(screenId);

    for (const index of sectionIndexes) {
      const curArr = curScreen[index] ?? [];
      const initArr = initScreen[index] ?? [];

      const curMap = mapSectionsById(curArr);
      const initMap = mapSectionsById(initArr);

      const sectionIds = new Set([...curMap.keys(), ...initMap.keys()]);

      for (const sectionId of sectionIds) {
        const curSection = curMap.get(sectionId);
        const initSection = initMap.get(sectionId);

        const sectionContext = {
          ...(curSection || initSection),
          channel_screen_sequence: Number(index) + 1,
          position: position?.position,
          duration: position?.duration,
        };

        if (!curSection && initSection) {
          changes.push(
            ...diffComponents([], initSection.components ?? [], sectionContext),
          );
        } else if (curSection && !initSection) {
          changes.push(
            ...diffComponents(curSection.components ?? [], [], sectionContext),
          );
        } else if (curSection && initSection) {
          changes.push(
            ...diffComponents(
              curSection.components ?? [],
              initSection.components ?? [],
              sectionContext,
            ),
          );
        }
      }
    }
  }

  return changes;
};

export const collectAllComponentsFromGrid = (grid: TScreensGridSections) => {
  const map = new Map<
    number,
    IScheduledSectionComponent & { position: number }
  >();

  Object.entries(grid).forEach(([pos, screenData]) => {
    const position = Number(pos);
    Object.values(screenData).forEach((sections) => {
      sections.forEach((section) => {
        section.components?.forEach((comp) => {
          if (comp.id) {
            map.set(comp.id, { ...comp, position });
          }
        });
      });
    });
  });

  return map;
};

export const getMinStartDate = (changes: ChangeItem[] = []): string | null => {
  const dates = filterInvalidChanges(changes)
    .map((c) => c.data?.start_date)
    .map((d) => d.trim());

  if (dates.length === 0) return null;

  let earliest = dates[0];
  for (const d of dates) {
    if (dayjs(d).isBefore(dayjs(earliest))) earliest = d;
  }

  return dayjs(earliest).format(ISO_DATE_FORMAT);
};

export const enrichChangesWithComponentData = (
  changes: ChangeItem[],
  screensGridSections: TScreensGridSections,
): ChangeItem[] => {
  const componentMap = collectAllComponentsFromGrid(screensGridSections);

  return changes.map((change) => {
    const comp = componentMap.get(change.data?.id);

    if (!comp) return change;

    return {
      ...change,
      data: {
        ...change.data,
        start_date: change.data?.start_date ?? comp.scheduleStartDate,
        end_date: change.data?.end_date ?? comp.scheduleEndDate,
        position: change.data?.position ?? comp.position,
      },
    };
  });
};

export const haveScheduleItemsInPosition = (
  curPosition: number,
  screensGridSections: TScreensGridSections,
) => {
  for (const sections of Object.values(screensGridSections[curPosition])) {
    for (const item of sections) {
      if (!isEmpty(item.components)) {
        return true;
      }
    }
  }

  return false;
};

export const filterOutInvalidDurationChanges = (
  screensGridSectionChanges: ChangeItem[],
  screensGridSections: TScreensGridSections,
) => {
  const filteredScreensGridSectionChanges = [];

  for (const change of screensGridSectionChanges) {
    const changeDataKeys = Object.keys(change.data);

    if (
      changeDataKeys.length === 2 &&
      changeDataKeys.includes('duration') &&
      !haveScheduleItemsInPosition(change.data.position, screensGridSections)
    ) {
      continue;
    }

    filteredScreensGridSectionChanges.push(change);
  }

  return filteredScreensGridSectionChanges;
};

export const filterInvalidChanges = (changes: ChangeItem[]): ChangeItem[] => {
  const cutoffDate = dayjs().subtract(30, 'days');

  return changes.filter((change) => {
    const endDate = change.data?.end_date;
    const startDate = change.data?.start_date;

    // If end_date is present and invalid (before 30 days ago), filter it out
    if (endDate && dayjs(endDate).isBefore(cutoffDate)) {
      return false;
    }

    // If start_date is present and invalid, filter it out
    if (!startDate || !dayjs(startDate).isValid()) {
      return false;
    }

    return true;
  });
};

export const getNavigateBackLink = (location: any, locationGroupId: string) => {
  const navigatedFromScheduleComponentListview =
    location.state?.from === NAVIGATION_STATE.FROM_SCHEDULE_COMPONENT_LISTVIEW;
  const navigateBackLink = navigatedFromScheduleComponentListview
    ? PATHS.LOCATION_GROUP_SCHEDULE(locationGroupId)
    : PATHS.LOCATION_GROUP_CALENDAR(locationGroupId);

  return navigateBackLink;
};
